import {useState, useMemo, useRef, useEffect, useCallback} from "react";
import {useDebouncedDocumentTitleSuggestion} from "@/hooks/useDebouncedDocumentTitleSuggestion";
import {useViewPreferences, type SortDirection} from "@/hooks/use-view-preferences";
import {useDynamicTruncate, truncateText} from "@/hooks/use-dynamic-truncate";
import {useMutation, useQuery} from "@tanstack/react-query";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Textarea} from "@/components/ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {Collapsible, CollapsibleContent} from "@/components/ui/collapsible";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs";
import {useToast} from "@/hooks/use-toast";
import {queryClient} from "@/lib/queryClient";
import {knowledgeBaseService, type KnowledgeBaseItem} from "@/services/knowledgeBaseService";
import {
    effectiveItemActions,
    type ItemOverrideMap,
    type SpaceMemberRole,
} from "@/services/sharedSpacePermissionService";
import {documentLinkingService, type EntityType, type LinkedEntity} from "@/services/documentLinkingService";
import {ragService} from "@/services/ragService";
import {
    FileText,
    Plus,
    Edit,
    Trash2,
    FileCheck,
    Lightbulb,
    Target,
    Users,
    Calendar,
    Code,
    Presentation,
    ExternalLink,
    Link,
    MessageSquare,
    BookOpen,
    Search,
    Grid3X3,
    List,
    SortAsc,
    SortDesc,
    Filter,
    X,
    ChevronDown,
    ChevronRight,
    Bookmark,
    BookmarkCheck,
    Layers,
    FolderOpen,
    // StickyNote,
    // Pin,
    Link2,
    MoreVertical,
    ArrowRight,
    ArrowUp,
    ArrowDown,
    Sparkles
} from "lucide-react";
import {formatDistanceToNow} from "date-fns";
import {RichTextEditor} from "./RichTextEditor";
import {DocumentLinkerDrawer} from "./DocumentLinkerDrawer";
import {ThreadLinkingDialog} from "./ThreadLinkingDialog";
import {FileUploadToNote} from "./FileUploadToNote";
import {GoogleDrivePicker} from "./GoogleDrivePicker";
import {topicsService} from "@/services/topicsService";
import {mastermindSpacesService} from "@/services/mastermindSpacesService";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import type {Topic} from "@shared/schema";
import type {MastermindSpace} from "@shared/schema";
import {marked} from "marked";
import DOMPurify from "dompurify";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel
} from "@/components/ui/dropdown-menu";
import {Switch} from "@/components/ui/switch";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {SiGoogledrive} from "react-icons/si";
import {useLibrary} from "@/contexts/LibraryContext";
import {useMastermind} from "@/contexts/MastermindContext";
import {useAuth} from "@/contexts/AuthContext";
import { partitionMerged, isKnowledgeDocMerged } from "@/lib/spaceMergeUi";
import { SpaceMergedSection, MergedRowChrome } from "@/components/space-merge";
import {DocumentItem} from "./DocumentItem";
import {DocumentViewerModal} from "./DocumentViewerModal";
import {PinVerificationDialog} from "./PinVerificationDialog";
import {pinService} from "@/services/pinService";

const stripHtml = (html: string | null | undefined): string => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
};

const isHtml = (str: string): boolean => {
    return /<[a-z][\s\S]*>/i.test(str);
};

const ensureHtmlContent = (content: string | null | undefined): string => {
    if (!content) return "";
    if (isHtml(content)) return content;
    try {
        const html = marked.parse(content, {async: false}) as string;
        return DOMPurify.sanitize(html);
    } catch (error) {
        const escaped = content
            .split('\n\n')
            .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
            .join('');
        return escaped;
    }
};

const documentTypes = [
    {value: "note", label: "Note", icon: FileText, color: "hsl(220, 70%, 50%)", description: "Quick notes and ideas"},
    {
        value: "research",
        label: "Research",
        icon: FileCheck,
        color: "hsl(280, 67%, 55%)",
        description: "Research findings and analysis"
    },
    {
        value: "plan",
        label: "Plan",
        icon: Target,
        color: "hsl(142, 71%, 45%)",
        description: "Strategic plans and roadmaps"
    },
    {
        value: "idea",
        label: "Idea",
        icon: Lightbulb,
        color: "hsl(45, 93%, 47%)",
        description: "Creative ideas and concepts"
    },
    {
        value: "meeting",
        label: "Meeting Notes",
        icon: Users,
        color: "hsl(24, 95%, 53%)",
        description: "Meeting summaries and action items"
    },
    {
        value: "journal",
        label: "Journal",
        icon: Calendar,
        color: "hsl(0, 84%, 60%)",
        description: "Personal journal entries"
    },
    {
        value: "code",
        label: "Code Snippet",
        icon: Code,
        color: "hsl(271, 76%, 53%)",
        description: "Code examples and snippets"
    },
    {
        value: "presentation",
        label: "Presentation",
        icon: Presentation,
        color: "hsl(199, 89%, 48%)",
        description: "Presentation outlines and slides"
    },
];


function DocumentLinkIndicators({documentId}: { documentId: string }) {
    const {data: links} = useQuery({
        queryKey: [`/api/documents/${documentId}/links`],
    });

    const chatThreadCount = (links as any)?.chatThreads?.length || 0;
    const sourceCount = (links as any)?.sources?.length || 0;
    const knowledgeItemCount = (links as any)?.knowledgeItems?.length || 0;

    if (chatThreadCount === 0 && sourceCount === 0 && knowledgeItemCount === 0) {
        return null;
    }

    return (
        <div className="flex gap-2 text-xs text-muted-foreground">
            {chatThreadCount > 0 && (
                <div className="flex items-center gap-1" data-testid={`link-indicator-chats-${documentId}`}>
                    <MessageSquare className="h-3 w-3"/>
                    <span>{chatThreadCount}</span>
                </div>
            )}
            {sourceCount > 0 && (
                <div className="flex items-center gap-1" data-testid={`link-indicator-sources-${documentId}`}>
                    <Link className="h-3 w-3"/>
                    <span>{sourceCount}</span>
                </div>
            )}
            {knowledgeItemCount > 0 && (
                <div className="flex items-center gap-1" data-testid={`link-indicator-knowledge-${documentId}`}>
                    <BookOpen className="h-3 w-3"/>
                    <span>{knowledgeItemCount}</span>
                </div>
            )}
        </div>
    );
}

interface SourceLinksProps {
    documentId: string;
    isSource: boolean;
    onToggleSource: (isSource: boolean) => void;
}

function SourceLinks({documentId, isSource, onToggleSource}: SourceLinksProps) {
    const {toast} = useToast();
    const [isExpanded, setIsExpanded] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<LinkedEntity[]>([]);

    const {data: linkedEntities = [], refetch: refetchLinks} = useQuery<LinkedEntity[]>({
        queryKey: ["document-entity-links", documentId],
        queryFn: async () => {
            return await documentLinkingService.getLinkedEntitiesForDocument(documentId);
        },
        enabled: isSource,
    });

    const toggleSourceMutation = useMutation({
        mutationFn: async (newIsSource: boolean) => {
            return await knowledgeBaseService.toggleDocumentAsSource(documentId, newIsSource);
        },
        onSuccess: (_, newIsSource) => {
            onToggleSource(newIsSource);
            toast({
                title: newIsSource ? "Document marked as source" : "Source status removed",
                description: newIsSource
                    ? "You can now link this document to spaces, topics, and threads"
                    : "Document is no longer a linkable source",
            });
            queryClient.invalidateQueries({ queryKey: ["knowledgebase-summary"] });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to update source status",
                variant: "destructive",
            });
        },
    });

    const createLinkMutation = useMutation({
        mutationFn: async (entity: LinkedEntity) => {
            return await documentLinkingService.createLink({
                documentId,
                entityType: entity.type,
                entityId: entity.id,
                linkType: "reference",
            });
        },
        onSuccess: () => {
            toast({title: "Link created"});
            refetchLinks();
            setSearchQuery("");
            setSearchResults([]);
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to create link",
                variant: "destructive",
            });
        },
    });

    const deleteLinkMutation = useMutation({
        mutationFn: async (linkId: string) => {
            return await documentLinkingService.deleteLink(linkId);
        },
        onSuccess: () => {
            toast({title: "Link removed"});
            refetchLinks();
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to remove link",
                variant: "destructive",
            });
        },
    });

    const handleSearch = async (query: string) => {
        setSearchQuery(query);
        if (query.trim().length < 2) {
            setSearchResults([]);
            return;
        }
        try {
            const results = await documentLinkingService.searchEntities(query, undefined, documentId);
            setSearchResults(results);
        } catch {
            setSearchResults([]);
        }
    };

    const getEntityIcon = (type: EntityType) => {
        switch (type) {
            case "space":
                return <Layers className="h-3 w-3"/>;
            case "topic":
                return <FolderOpen className="h-3 w-3"/>;
            case "thread":
                return <MessageSquare className="h-3 w-3"/>;
            default:
                return <Link className="h-3 w-3"/>;
        }
    };

    return (
        <div className="mt-2">
            <div className="flex items-center gap-2">
                <Button
                    variant={isSource ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs gap-1"
                    onClick={() => toggleSourceMutation.mutate(!isSource)}
                    data-testid={`button-toggle-source-${documentId}`}
                >
                    {isSource ? <BookmarkCheck className="h-3 w-3"/> : <Bookmark className="h-3 w-3"/>}
                    {isSource ? "Source" : "Make Source"}
                </Button>

                {isSource && linkedEntities.length > 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => setIsExpanded(!isExpanded)}
                        data-testid={`button-expand-links-${documentId}`}
                    >
                        {isExpanded ? <ChevronDown className="h-3 w-3"/> : <ChevronRight className="h-3 w-3"/>}
                        {linkedEntities.length} linked
                    </Button>
                )}
            </div>

            {isSource && (
                <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                    <CollapsibleContent className="mt-2 space-y-2">
                        {/* Search for entities to link */}
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground"/>
                            <Input
                                placeholder="Search spaces, topics, threads..."
                                value={searchQuery}
                                onChange={(e) => handleSearch(e.target.value)}
                                className="pl-7 h-7 text-xs"
                                data-testid={`input-search-entities-${documentId}`}
                            />
                        </div>

                        {/* Search results */}
                        {searchResults.length > 0 && (
                            <div className="border rounded-md divide-y max-h-32 overflow-y-auto">
                                {searchResults.map((entity) => (
                                    <div
                                        key={`${entity.type}-${entity.id}`}
                                        className="flex items-center justify-between px-2 py-1.5 text-xs hover-elevate"
                                    >
                                        <div className="flex items-center gap-2">
                                            {getEntityIcon(entity.type)}
                                            <span className="truncate">{entity.name}</span>
                                            <Badge variant="outline" className="text-[10px] px-1">{entity.type}</Badge>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-5 px-1"
                                            onClick={() => createLinkMutation.mutate(entity)}
                                            data-testid={`button-link-entity-${entity.id}`}
                                        >
                                            <Plus className="h-3 w-3"/>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Linked entities */}
                        {linkedEntities.length > 0 && (
                            <div className="border rounded-md divide-y">
                                {linkedEntities.map((entity) => (
                                    <div
                                        key={entity.linkId}
                                        className="flex items-center justify-between px-2 py-1.5 text-xs"
                                    >
                                        <div className="flex items-center gap-2">
                                            {getEntityIcon(entity.type)}
                                            <span className="truncate">{entity.name}</span>
                                            <Badge variant="outline" className="text-[10px] px-1">{entity.type}</Badge>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-5 px-1 text-destructive"
                                            onClick={() => deleteLinkMutation.mutate(entity.linkId)}
                                            data-testid={`button-unlink-entity-${entity.linkId}`}
                                        >
                                            <X className="h-3 w-3"/>
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {linkedEntities.length === 0 && !searchQuery && (
                            <p className="text-xs text-muted-foreground text-center py-2">
                                Search above to link this source to spaces, topics, or threads
                            </p>
                        )}
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}

interface DocumentGridProps {
    spaceId?: string;
    spaceNotes?: string;
    spaceName?: string;
    topics?: Topic[];
    space?: MastermindSpace | null;
    allSpaces?: MastermindSpace[];
    allDocuments?: KnowledgeBaseItem[];
    spaceMemberRole?: SpaceMemberRole | null;
    spaceOwnerUserId?: string | null;
    itemOverrides?: ItemOverrideMap;
}

type SortField = "title" | "updatedAt" | "type";
type ViewMode = "grid" | "list";

export function DocumentGrid({isLoading, refreshKnowledge, spaceId, spaceNotes, spaceName, topics: topicsProp, space: spaceProp, allSpaces: allSpacesProp, allDocuments, spaceMemberRole = null, spaceOwnerUserId = null, itemOverrides}: DocumentGridProps) {
    const {toast} = useToast();
    const {user} = useAuth();
    const {currentWorkspaceId} = useWorkspace();
    const {setSelectedDocumentId, setDocumentEditSpaceContextId} = useLibrary();
    const {setIsCollapsed} = useMastermind();
    const [showPinDialogForDoc, setShowPinDialogForDoc] = useState(false);
    const [pendingDocumentIdToOpen, setPendingDocumentIdToOpen] = useState<string | null>(null);
    const [unlockedDocumentIds, setUnlockedDocumentIds] = useState<Set<string>>(() => new Set());
    const [documentToView, setDocumentToView] = useState<KnowledgeBaseItem | null>(null);

    const {data: pinStatus} = useQuery({
        queryKey: ["pin-status"],
        queryFn: () => pinService.getStatus(),
    });

    // Need space / isSharedSpace early so handleDocumentClick can branch for shared docs
    const {data: spaceFromQuery} = useQuery<MastermindSpace | null>({
        queryKey: ["/api/spaces", spaceId],
        queryFn: async () => {
            if (!spaceId) return null;
            return await mastermindSpacesService.getMastermindSpace(spaceId);
        },
        enabled: !!spaceId && spaceProp === undefined,
    });
    const spaceForClick = spaceProp ?? spaceFromQuery;
    const isSharedSpace = !!(spaceForClick as { isShared?: boolean } | null)?.isShared;

    const handleDocumentClick = useCallback(
        (documentId: string, isLocked?: boolean) => {
            const doc = allDocuments?.find((d) => d.id === documentId);
            if (isSharedSpace && doc && doc.userId !== user?.id) {
                const actions = effectiveItemActions(
                    spaceMemberRole,
                    "document",
                    documentId,
                    doc.userId,
                    user?.id,
                    spaceOwnerUserId,
                    itemOverrides,
                );
                if (!actions.canEdit) {
                    setDocumentToView(doc);
                    return;
                }
            }
            if (isLocked && !pinStatus?.isSessionValid) {
                setPendingDocumentIdToOpen(documentId);
                setShowPinDialogForDoc(true);
                return;
            }
            if (isLocked) {
                setUnlockedDocumentIds(prev => new Set(prev).add(documentId));
            }
            if (spaceId) {
                setDocumentEditSpaceContextId(spaceId);
            }
            setIsCollapsed(false);
            setSelectedDocumentId(documentId);
        },
        [allDocuments, isSharedSpace, user?.id, pinStatus?.isSessionValid, setIsCollapsed, setSelectedDocumentId, setDocumentEditSpaceContextId, spaceId, spaceMemberRole, spaceOwnerUserId, itemOverrides]
    );

    const handleDocPinSuccess = useCallback(() => {
        if (pendingDocumentIdToOpen) {
            if (spaceId) {
                setDocumentEditSpaceContextId(spaceId);
            }
            setUnlockedDocumentIds(prev => new Set(prev).add(pendingDocumentIdToOpen));
            setIsCollapsed(false);
            setSelectedDocumentId(pendingDocumentIdToOpen);
            setPendingDocumentIdToOpen(null);
            setShowPinDialogForDoc(false);
        }
    }, [pendingDocumentIdToOpen, setIsCollapsed, setSelectedDocumentId, setDocumentEditSpaceContextId, spaceId]);

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isMoveToSpaceDialogOpen, setIsMoveToSpaceDialogOpen] = useState(false);
    const [documentToMove, setDocumentToMove] = useState<KnowledgeBaseItem | null>(null);
    const [selectedSpaceId, setSelectedSpaceId] = useState<string>("");
    const [formData, setFormData] = useState({
        title: "",
        type: "note",
        content: "",
    });

    const {syncTitleRef, onUserEditedTitle, resetSuggestionState} = useDebouncedDocumentTitleSuggestion({
        contentHtml: formData.content,
        setTitle: (value) =>
            setFormData((prev) => ({
                ...prev,
                title: typeof value === "function" ? (value as (t: string) => string)(prev.title) : value,
            })),
        enabled: isCreateDialogOpen,
        debounceMs: 1000,
    });

    useEffect(() => {
        syncTitleRef(formData.title);
    }, [formData.title, syncTitleRef]);

    useEffect(() => {
        if (isCreateDialogOpen) {
            resetSuggestionState();
        }
    }, [isCreateDialogOpen, resetSuggestionState]);

    // Search, sort, filter, and view mode state
    const [searchQuery, setSearchQuery] = useState("");
    const [filterType, setFilterType] = useState<string | null>(null);
    const {
        viewMode,
        setViewMode,
        sortField,
        setSortField,
        sortDirection,
        setSortDirection,
        toggleSortDirection
    } = useViewPreferences<SortField>({
        storageKey: "documents",
        defaultViewMode: "grid",
        defaultSortField: "updatedAt",
        defaultSortDirection: "desc",
    });

    // Dynamic truncation for list view - measure the text container width
    const listViewContainerRef = useRef<HTMLDivElement>(null);
    const [hasAttachedRef, setHasAttachedRef] = useState(false);

    const titleTruncate = useDynamicTruncate({
        fontSize: 14, // text-sm
        fontWeight: 500, // font-medium
        padding: 16, // Account for gaps and padding
        minChars: 15,
        maxChars: 150,
    });
    const contentTruncate = useDynamicTruncate({
        fontSize: 12, // text-xs
        fontWeight: 400,
        padding: 16,
        minChars: 20,
        maxChars: 200,
    });

    // Use topics from prop if provided, otherwise fetch (for backward compatibility)
    const {data: topicsFromQuery = []} = useQuery<Topic[]>({
        queryKey: ["/api/spaces", spaceId, "topics"],
        queryFn: async () => {
            if (!spaceId) return [];
            return await topicsService.getTopics(spaceId);
        },
        enabled: !!spaceId && topicsProp === undefined,
    });
    const topics = topicsProp ?? topicsFromQuery;
    const spaceLinkedDocuments = [];

    // space / spaceFromQuery defined above for handleDocumentClick; reuse here
    const space = spaceProp ?? spaceFromQuery;

    // Use allSpaces from prop if provided, otherwise fetch (for backward compatibility, scoped by workspace)
    const {data: allSpacesFromQuery = []} = useQuery<MastermindSpace[]>({
        queryKey: ["/api/spaces", currentWorkspaceId ?? "personal"],
        queryFn: async () => {
            return await mastermindSpacesService.getMastermindSpaces(currentWorkspaceId ?? null);
        },
        enabled: allSpacesProp === undefined,
    });
    const allSpaces = allSpacesProp ?? allSpacesFromQuery;

    // Get documents linked to this space via linked_source_ids
    const spaceLinkedDocumentIds = useMemo(() => {
        if (!space?.linkedSourceIds) return new Set<string>();
        // linkedSourceIds might contain document IDs (check if they exist in allDocuments)
        const linkedIds = new Set<string>();
        space.linkedSourceIds.forEach(id => {
            // Check if this ID exists in our allDocuments (it's a document ID)
            if (allDocuments.some((doc: KnowledgeBaseItem) => doc.id === id)) {
                linkedIds.add(id);
            }
        });
        return linkedIds;
    }, [space?.linkedSourceIds, allDocuments]);
    // Get all document IDs linked to this space (via linked_source_ids or document_entity_links)
    const allSpaceLinkedDocumentIds = useMemo(() => {
        const ids = new Set(spaceLinkedDocumentIds);
        spaceLinkedDocuments.forEach(doc => ids.add(doc.id));
        // Also include documents that have this spaceId
        allDocuments.forEach(doc => {
            if (doc.spaceId === spaceId) {
                ids.add(doc.id);
            }
        });
        return ids;
    }, [spaceLinkedDocumentIds, spaceLinkedDocuments, allDocuments, spaceId]);

    // Create a Set of topic IDs in this space for efficient lookup
    const spaceTopicIds = useMemo(() => {
        return new Set(topics.map(topic => topic.id));
    }, [topics]);

    // Helper function to get spaceIds from document (supports both array and single value for backward compatibility)
    const getDocumentSpaceIds = useCallback((doc: KnowledgeBaseItem): string[] => {
        const metadata = doc.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        // Check for spaceIds array first (new format)
        if (Array.isArray(parsedMetadata.spaceIds)) {
            return parsedMetadata.spaceIds.map((id: any) => String(id)).filter((id: string) => id !== 'null' && id !== 'undefined');
        }
        
        // Fallback to single spaceId in metadata (backward compatibility - more reliable than doc.spaceId)
        if (parsedMetadata.spaceId) {
            return [String(parsedMetadata.spaceId)];
        }
        
        // Last fallback to doc.spaceId (for very old documents)
        if (doc.spaceId) {
            return [String(doc.spaceId)];
        }
        
        return [];
    }, []);

    // Helper function to get topicIds from document (supports both array and single value for backward compatibility)
    const getDocumentTopicIds = useCallback((doc: KnowledgeBaseItem): string[] => {
        const metadata = doc.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        // Check for topicIds array first (new format)
        if (Array.isArray(parsedMetadata.topicIds)) {
            return parsedMetadata.topicIds.map((id: any) => String(id)).filter((id: string) => id !== 'null' && id !== 'undefined');
        }
        
        // Fallback to single topicId in metadata (backward compatibility - more reliable than doc.topicId)
        if (parsedMetadata.topicId) {
            return [String(parsedMetadata.topicId)];
        }
        
        return [];
    }, []);

    // Filter linked documents from allDocuments client-side
    // Include documents that are:
    // 1. Directly linked to this space (via spaceIds array, spaceId, or linkedSourceIds)
    // 2. OR linked to any topic in this space (via topicIds array or topicId)
    // Note: Supports both spaceIds/topicIds arrays (new) and spaceId/topicId single values (backward compatibility)
    const linkedDocuments = useMemo(() => {
        if (!spaceId) return allDocuments;
        
        return allDocuments.filter((doc) => {
            // Check if document is directly linked to this space
            // Support both spaceIds array (new) and spaceId single value (backward compatibility)
            const docSpaceIds = getDocumentSpaceIds(doc);
            const isDirectlyLinked = docSpaceIds.some(id => String(id) === String(spaceId)) 
                || doc.spaceId === spaceId 
                || allSpaceLinkedDocumentIds.has(doc.id);
            
            // Check if document is linked to any topic in this space
            // Support both topicIds array (new) and topicId single value (backward compatibility)
            const docTopicIds = getDocumentTopicIds(doc);
            const isLinkedToSpaceTopic = topics.some(topic => {
                const topicIdStr = String(topic.id);
                return docTopicIds.some(id => String(id) === topicIdStr);
            });
            
            return isDirectlyLinked || isLinkedToSpaceTopic;
        });
    }, [allDocuments, spaceId, allSpaceLinkedDocumentIds, topics, getDocumentSpaceIds, getDocumentTopicIds]);

    // Create a map of topicId to topic name for displaying topic names in documents
    // Support both string and number keys for topicId matching
    const topicNameMap = useMemo(() => {
        const map: Record<string | number, string> = {};
        topics.forEach(topic => {
            map[topic.id] = topic.name;
            // Also add as number if topic.id is a string, for compatibility
            if (typeof topic.id === 'string') {
                const numId = Number(topic.id);
                if (!isNaN(numId)) {
                    map[numId] = topic.name;
                }
            }
        });
        return map;
    }, [topics]);

    // Embedding status for RAG (embed / include in RAG in 3-dot menu)
    const linkedDocumentIds = useMemo(() => linkedDocuments.map((d) => d.id), [linkedDocuments]);
    const { data: embeddingStatusMap = {} } = useQuery<Record<string, boolean>>({
        queryKey: ["embedding-status-docgrid", spaceId ?? "", linkedDocumentIds.slice().sort().join(",")],
        queryFn: () => ragService.getEmbeddingStatusForDocuments(linkedDocumentIds),
        enabled: linkedDocumentIds.length > 0,
        staleTime: 30000,
    });

    // Filter and sort documents
    const filteredAndSortedDocuments = useMemo(() => {
        let result = [...linkedDocuments];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(doc =>
                (doc.title.toLowerCase().includes(query) ||
                    stripHtml(doc.content).toLowerCase().includes(query))
            );
        }

        // Apply type filter
        if (filterType) {
            result = result.filter(doc => doc.type === filterType);
        }

        // Apply sorting
        result.sort((a, b) => {
            let comparison = 0;
            switch (sortField) {
                case "title":
                    comparison = (a.title || "").localeCompare(b.title || "");
                    break;
                case "type":
                    comparison = (a.type || "note").localeCompare(b.type || "note");
                    break;
                case "updatedAt":
                default:
                    const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
                    const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
                    comparison = aTime - bTime;
                    break;
            }
            return sortDirection === "asc" ? comparison : -comparison;
        });

        return result;
    }, [linkedDocuments, searchQuery, filterType, sortField, sortDirection]);

    const { native: filteredSortedNativeDocs, merged: filteredSortedMergedDocs } = useMemo(
        () => partitionMerged(filteredAndSortedDocuments, isKnowledgeDocMerged),
        [filteredAndSortedDocuments]
    );

    const hasAnyFilteredDocs =
        filteredSortedNativeDocs.length > 0 || filteredSortedMergedDocs.length > 0;

    // Reset ref attachment when documents change or view mode changes
    useEffect(() => {
        setHasAttachedRef(false);
    }, [filteredAndSortedDocuments.length, viewMode]);

    const createDocumentMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            const docData = {
                title: data.title,
                type: data.type,
                content: data.content,
                spaceId: spaceId || null,
                topicId: null,
                tagIds: [],
            };
            return await knowledgeBaseService.createKnowledgeBaseItem(docData, currentWorkspaceId ?? null);
        },
        onSuccess: () => {
            refreshKnowledge();
            toast({
                title: "Document Created",
                description: "Your document has been created successfully",
            });
            setIsCreateDialogOpen(false);
            setFormData({title: "", type: "note", content: ""});
        },
        onError: () => {
            toast({
                title: "Creation Failed",
                description: "There was an error creating your document",
                variant: "destructive",
            });
        },
    });

    const updateDocumentMutation = useMutation({
        mutationFn: async ({id, data}: { id: string; data: Partial<KnowledgeBaseItem> }) => {
            const docData = {
                title: data.title,
                type: data.type,
                content: data.content,
                spaceId: data.spaceId || spaceId || null,
                topicId: data.topicId || null,
                tagIds: data.tagIds || [],
            };
            const result = await knowledgeBaseService.updateKnowledgeBaseItem(id, docData);
            if (!result) throw new Error("Document not found");
            return result;
        },
        onSuccess: () => {
            refreshKnowledge();
            toast({
                title: "Document Updated",
                description: "Your document has been updated successfully",
            });
        },
        onError: () => {
            toast({
                title: "Update Failed",
                description: "There was an error updating your document",
                variant: "destructive",
            });
        },
    });


    // Unlink document from topic mutation
    const unlinkFromTopicMutation = useMutation({
        mutationFn: async (documentId: string) => {
            return await knowledgeBaseService.updateKnowledgeBaseItem(documentId, {
                topicId: null
            });
        },
        onSuccess: () => {
            refreshKnowledge();
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId]});
            toast({
                title: "Document Unlinked",
                description: "Document has been unlinked from topic",
            });
        },
        onError: () => {
            toast({
                title: "Unlink Failed",
                description: "There was an error unlinking the document",
                variant: "destructive",
            });
        },
    });

    // Unlink document from space mutation (via document_entity_links, spaceId, or linked_source_ids)
    const unlinkFromSpaceMutation = useMutation({
        mutationFn: async (documentId: string) => {
            if (!spaceId || !space) throw new Error("Space ID is required");

            // 1. Remove from document_entity_links if exists
            const links = await documentLinkingService.getLinksForDocument(documentId);
            const spaceLink = links.find(link => link.entityType === "space" && link.entityId === spaceId);
            if (spaceLink) {
                await documentLinkingService.deleteLink(spaceLink.id);
            }

            // 2. Clear spaceId on document if it matches
            const doc = allDocuments.find((d: KnowledgeBaseItem) => d.id === documentId);
            if (doc?.spaceId === spaceId) {
                await knowledgeBaseService.updateKnowledgeBaseItem(documentId, {
                    spaceId: null
                });
            }

            // 3. Remove from space's linked_source_ids if it's in there
            if (space.linkedSourceIds && space.linkedSourceIds.includes(documentId)) {
                const updatedLinkedSourceIds = space.linkedSourceIds.filter(id => id !== documentId);
                await mastermindSpacesService.updateMastermindSpace(spaceId, {
                    linkedSourceIds: updatedLinkedSourceIds
                });
            }
        },
        onSuccess: () => {
            refreshKnowledge();
            queryClient.invalidateQueries({queryKey: ["/api/document-links", "space", spaceId]});
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId]});
            toast({
                title: "Document Unlinked",
                description: "Document has been unlinked from space",
            });
        },
        onError: () => {
            toast({
                title: "Unlink Failed",
                description: "There was an error unlinking the document",
                variant: "destructive",
            });
        },
    });

    // Move document to another space mutation
    const moveToSpaceMutation = useMutation({
        mutationFn: async ({documentId, targetSpaceId}: { documentId: string; targetSpaceId: string }) => {
            const result = await knowledgeBaseService.updateKnowledgeBaseItem(documentId, {
                spaceId: targetSpaceId,
            });
            if (!result) throw new Error("Document not found");
            return result;
        },
        onSuccess: () => {
            refreshKnowledge();
            queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId]});
            toast({
                title: "Document Moved",
                description: "Document has been moved to the selected space",
            });
            setIsMoveToSpaceDialogOpen(false);
            setDocumentToMove(null);
            setSelectedSpaceId("");
        },
        onError: () => {
            toast({
                title: "Move Failed",
                description: "There was an error moving the document",
                variant: "destructive",
            });
        },
    });

    const handleOpenMoveToSpace = (doc: KnowledgeBaseItem) => {
        setDocumentToMove(doc);
        setSelectedSpaceId("");
        setIsMoveToSpaceDialogOpen(true);
    };


    const handleMoveToSpace = () => {
        if (!documentToMove || !selectedSpaceId) {
            toast({
                title: "Selection Required",
                description: "Please select a space to move the document to",
                variant: "destructive",
            });
            return;
        }
        moveToSpaceMutation.mutate({
            documentId: documentToMove.id,
            targetSpaceId: selectedSpaceId,
        });
    };

    const handleCreateDocument = () => {
        if (!formData.title.trim()) {
            toast({
                title: "Title Required",
                description: "Please enter a title for your document",
                variant: "destructive",
            });
            return;
        }
        createDocumentMutation.mutate(formData);
    };



    if (isLoading) {
        return <div className="text-muted-foreground text-center py-8">Loading documents...</div>;
    }

    const toggleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const clearFilters = () => {
        setSearchQuery("");
        setFilterType(null);
        setSortField("updatedAt");
        setSortDirection("desc");
    };

    const hasActiveFilters = searchQuery.trim() || filterType;

    return (
        <div className="space-y-4 flex flex-col h-full" style={{height: 'calc(100vh - 100px)'}}>
            {/* Header Row 1: Title and New Button */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="font-semibold">Documents</h3>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size="sm" className="h-8" data-testid="button-create-document">
                            <Plus className="h-3.5 w-3.5 mr-1"/>
                            <span className="hidden sm:inline">New</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setIsCreateDialogOpen(true)}>
                            <FileText className="h-4 w-4 mr-2" />
                            New Document
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                            <FileUploadToNote
                                spaceId={spaceId || null}
                                folderId={null}
                                topicId={null}
                                buttonLabel="Upload Files"
                                multiple={true}
                                onComplete={() => {
                                    // Invalidate all knowledge base queries including the hook's query key
                                    queryClient.invalidateQueries({ 
                                        predicate: (query) => {
                                            const key = query.queryKey[0];
                                            return typeof key === 'string' && (
                                                key === '/api/knowledge-base' || 
                                                key === '/api/knowledgebase' ||
                                                key === 'knowledgebase' ||
                                                key === 'knowledgebase-summary'
                                            );
                                        }
                                    });
                                    // Also call refreshKnowledge to ensure real-time update
                                    refreshKnowledge();
                                }}
                            />
                        </div>
                        <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                            <GoogleDrivePicker
                                multiple={true}
                                isFinalConfirmation={true}
                                extractTextOnly={true}
                                onFilesSelected={async (files) => {
                                    // Validate files - skip invalid ones
                                    const validFiles = files.filter((file: any) => {
                                        // Check if it's an extracted text file (has text property)
                                        if (file.text && typeof file.text === 'string' && file.text.trim().length > 0) {
                                            return true;
                                        }
                                        // Skip files with errors or no text
                                        return false;
                                    });

                                    if (validFiles.length === 0) {
                                        toast({
                                            title: "No valid files",
                                            description: "No files with extractable text were found.",
                                            variant: "destructive",
                                        });
                                        return;
                                    }

                                    // Create notes for each file in the current space
                                    let successCount = 0;
                                    let failCount = 0;

                                    for (let i = 0; i < validFiles.length; i++) {
                                        const file = validFiles[i];
                                        const filename = file.filename || `Untitled Document ${i + 1}`;

                                        try {
                                            // Type guard: check if file has text property
                                            const fileContent = 'text' in file ? file.text : '';

                                            // Create document using knowledgeBaseService with spaceId
                                            await knowledgeBaseService.createKnowledgeBaseItem({
                                                title: filename,
                                                content: fileContent,
                                                type: 'note', // Default type
                                                spaceId: spaceId || null, // Use space ID
                                                topicId: null, // Default - no topic
                                                tagIds: [], // Default - no tags
                                                folderId: null, // Default - no folder
                                            }, currentWorkspaceId ?? null);

                                            successCount++;
                                        } catch (error: any) {
                                            console.error(`Error creating note for ${filename}:`, error);
                                            failCount++;
                                        }
                                    }

                                    // Invalidate all knowledge base queries including the hook's query key
                                    if (successCount > 0) {
                                        queryClient.invalidateQueries({ 
                                            predicate: (query) => {
                                                const key = query.queryKey[0];
                                                return typeof key === 'string' && (
                                                    key === '/api/knowledge-base' || 
                                                    key === '/api/knowledgebase' ||
                                                    key === 'knowledgebase' ||
                                                    key === 'knowledgebase-summary'
                                                );
                                            }
                                        });
                                        // Also call refreshKnowledge to ensure real-time update
                                        refreshKnowledge();
                                    }

                                    // Show completion message
                                    if (successCount > 0) {
                                        toast({
                                            title: "Notes created",
                                            description: `${successCount} note(s) created successfully${failCount > 0 ? `. ${failCount} failed.` : '.'}`,
                                        });
                                    }

                                    if (failCount > 0 && successCount === 0) {
                                        toast({
                                            title: "Creation failed",
                                            description: `Failed to create ${failCount} note(s).`,
                                            variant: "destructive",
                                        });
                                    }
                                }}
                            >
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                    <SiGoogledrive className="h-4 w-4 mr-2" />
                                    Add from Google Drive
                                </DropdownMenuItem>
                            </GoogleDrivePicker>
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Header Row 2: Search, Filter, Sort, View Toggle */}
            {linkedDocuments.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Search */}
                    <div className="relative flex-1 min-w-[200px] max-w-sm">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"/>
                        <Input
                            placeholder="Search documents..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-8 h-9 text-sm"
                            data-testid="input-search-documents"
                        />
                        {searchQuery && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute right-1 top-1 h-7 w-7"
                                onClick={() => setSearchQuery("")}
                                data-testid="button-clear-search"
                            >
                                <X className="h-4 w-4"/>
                            </Button>
                        )}
                    </div>

                    {/* Filter by Type */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-1 h-9" data-testid="button-filter-type">
                                <Filter className="h-3.5 w-3.5"/>
                                <span
                                    className="hidden sm:inline">{filterType ? documentTypes.find(t => t.value === filterType)?.label : "Type"}</span>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                            <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                            <DropdownMenuSeparator/>
                            <DropdownMenuItem
                                onClick={() => setFilterType(null)}
                                data-testid="filter-type-all"
                            >
                                All Types
                            </DropdownMenuItem>
                            {documentTypes.map((type) => (
                                <DropdownMenuItem
                                    key={type.value}
                                    onClick={() => setFilterType(type.value)}
                                    data-testid={`filter-type-${type.value}`}
                                >
                                    <type.icon className="h-4 w-4 mr-2" style={{color: type.color}}/>
                                    {type.label}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Sort - Using Select to match Topics/Threads */}
                    <Select 
                        value={`${sortField}-${sortDirection}`} 
                        onValueChange={(value) => {
                            const [field, direction] = value.split("-") as [SortField, SortDirection];
                            setSortField(field);
                            setSortDirection(direction);
                        }}
                    >
                        <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="select-sort-documents">
                            <SelectValue placeholder="Sort by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="title-asc" data-testid="sort-documents-title-asc">
                                <div className="flex items-center gap-2">
                                    <ArrowUp className="h-3 w-3" />
                                    <span>A → Z</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="title-desc" data-testid="sort-documents-title-desc">
                                <div className="flex items-center gap-2">
                                    <ArrowDown className="h-3 w-3" />
                                    <span>Z → A</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="updatedAt-desc" data-testid="sort-documents-updated-desc">
                                <div className="flex items-center gap-2">
                                    <ArrowDown className="h-3 w-3" />
                                    <span>Newest</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="updatedAt-asc" data-testid="sort-documents-updated-asc">
                                <div className="flex items-center gap-2">
                                    <ArrowUp className="h-3 w-3" />
                                    <span>Oldest</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="type-asc" data-testid="sort-documents-type-asc">
                                <div className="flex items-center gap-2">
                                    <ArrowUp className="h-3 w-3" />
                                    <span>Type A → Z</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="type-desc" data-testid="sort-documents-type-desc">
                                <div className="flex items-center gap-2">
                                    <ArrowDown className="h-3 w-3" />
                                    <span>Type Z → A</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    {/* View Toggle */}
                    <div className="flex items-center gap-0.5 border rounded-md p-0.5">
                        <Button
                            variant={viewMode === "list" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode("list")}
                            data-testid="button-view-list"
                            title="List view"
                        >
                            <List className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            variant={viewMode === "grid" ? "secondary" : "ghost"}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setViewMode("grid")}
                            data-testid="button-view-grid"
                            title="Column view"
                        >
                            <Grid3X3 className="h-3.5 w-3.5" />
                        </Button>
                    </div>

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <Button variant="ghost" size="sm" className="h-9 px-2" onClick={clearFilters}
                                data-testid="button-clear-filters">
                            <X className="h-3.5 w-3.5"/>
                        </Button>
                    )}

                    {/* Link Button - opens popup dialog */}
                    {/* {spaceId && topics.length > 0 && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="h-9"
                            onClick={() => setDocumentToLink({} as KnowledgeBaseItem)}
                            data-testid="button-link-documents"
                        >
                            <Link2 className="h-3.5 w-3.5 mr-1"/>
                            <span className="hidden sm:inline">Link Docs</span>
                        </Button>
                    )} */}
                </div>
            )}

            <div className="flex-1 overflow-y-auto pr-1 min-h-0">
                {linkedDocuments.length === 0 ? (
                    <Card>
                        <CardContent className="py-12 text-center">
                            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4"/>
                            <h3 className="text-lg font-semibold mb-2">No documents yet</h3>
                            <p className="text-muted-foreground mb-4">
                                Create your first document to start organizing your knowledge
                            </p>
                            <Button onClick={() => setIsCreateDialogOpen(true)}
                                    data-testid="button-create-first-document">
                                <Plus className="h-4 w-4 mr-2"/>
                                Create Document
                            </Button>
                        </CardContent>
                    </Card>
                ) : !hasAnyFilteredDocs ? (
                    <Card>
                        <CardContent className="py-8 text-center">
                            <Search className="h-10 w-10 mx-auto text-muted-foreground mb-3"/>
                            <h3 className="text-base font-semibold mb-2">No matching documents</h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Try adjusting your search or filter criteria
                            </p>
                            <Button variant="outline" onClick={clearFilters} data-testid="button-clear-filters-empty">
                                Clear Filters
                            </Button>
                        </CardContent>
                    </Card>
                ) : viewMode === "grid" ? (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                            {filteredSortedNativeDocs.map((doc) => (
                                <DocumentItem
                                    key={doc.id}
                                    document={doc}
                                    viewMode="grid"
                                    spaceId={spaceId}
                                    topics={topics}
                                    embeddingStatusMap={embeddingStatusMap}
                                    topicNameMap={topicNameMap}
                                    onRefresh={refreshKnowledge}
                                    onDocumentClick={handleDocumentClick}
                                    isUnlockedInSession={unlockedDocumentIds.has(doc.id)}
                                    isSharedSpace={isSharedSpace}
                                    spaceMemberRole={spaceMemberRole}
                                    spaceOwnerUserId={spaceOwnerUserId}
                                    itemOverrides={itemOverrides}
                                />
                            ))}
                        </div>
                        <SpaceMergedSection title="Merged documents" className="min-w-0" itemCount={filteredSortedMergedDocs.length}>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                                {filteredSortedMergedDocs.map((doc) => (
                                    <MergedRowChrome key={doc.id} merged showBadge>
                                        <DocumentItem
                                            document={doc}
                                            viewMode="grid"
                                            spaceId={spaceId}
                                            topics={topics}
                                            embeddingStatusMap={embeddingStatusMap}
                                            topicNameMap={topicNameMap}
                                            onRefresh={refreshKnowledge}
                                            onDocumentClick={handleDocumentClick}
                                            isUnlockedInSession={unlockedDocumentIds.has(doc.id)}
                                            isSharedSpace={isSharedSpace}
                                            spaceMemberRole={spaceMemberRole}
                                            spaceOwnerUserId={spaceOwnerUserId}
                                            itemOverrides={itemOverrides}
                                        />
                                    </MergedRowChrome>
                                ))}
                            </div>
                        </SpaceMergedSection>
                    </>
                ) : (
                    /* List View */
                    <div className="w-full max-w-4xl mx-auto pb-4" ref={listViewContainerRef}>
                        <div className="border rounded-md divide-y">
                            {filteredSortedNativeDocs.map((doc) => (
                                <DocumentItem
                                    key={doc.id}
                                    document={doc}
                                    viewMode="list"
                                    spaceId={spaceId}
                                    topics={topics}
                                    embeddingStatusMap={embeddingStatusMap}
                                    topicNameMap={topicNameMap}
                                    titleTruncate={titleTruncate}
                                    contentTruncate={contentTruncate}
                                    onRefresh={refreshKnowledge}
                                    onDocumentClick={handleDocumentClick}
                                    isUnlockedInSession={unlockedDocumentIds.has(doc.id)}
                                    onAttachRef={(el) => {
                                        if (el && !hasAttachedRef && titleTruncate.containerRef) {
                                            (titleTruncate.containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                            setHasAttachedRef(true);
                                        }
                                    }}
                                    hasAttachedRef={hasAttachedRef}
                                    isSharedSpace={isSharedSpace}
                                    spaceMemberRole={spaceMemberRole}
                                    spaceOwnerUserId={spaceOwnerUserId}
                                    itemOverrides={itemOverrides}
                                />
                            ))}
                        </div>
                        <SpaceMergedSection title="Merged documents" className="min-w-0" itemCount={filteredSortedMergedDocs.length}>
                            <div className="border rounded-md divide-y mt-3">
                                {filteredSortedMergedDocs.map((doc) => (
                                    <MergedRowChrome key={doc.id} merged showBadge>
                                        <DocumentItem
                                            document={doc}
                                            viewMode="list"
                                            spaceId={spaceId}
                                            topics={topics}
                                            embeddingStatusMap={embeddingStatusMap}
                                            topicNameMap={topicNameMap}
                                            titleTruncate={titleTruncate}
                                            contentTruncate={contentTruncate}
                                            onRefresh={refreshKnowledge}
                                            onDocumentClick={handleDocumentClick}
                                            isUnlockedInSession={unlockedDocumentIds.has(doc.id)}
                                            onAttachRef={(el) => {
                                                if (el && !hasAttachedRef && titleTruncate.containerRef) {
                                                    (titleTruncate.containerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                                    setHasAttachedRef(true);
                                                }
                                            }}
                                            hasAttachedRef={hasAttachedRef}
                                            isSharedSpace={isSharedSpace}
                                            spaceMemberRole={spaceMemberRole}
                                            spaceOwnerUserId={spaceOwnerUserId}
                                            itemOverrides={itemOverrides}
                                        />
                                    </MergedRowChrome>
                                ))}
                            </div>
                        </SpaceMergedSection>
                    </div>
                )}
            </div>

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Create New Document</DialogTitle>
                        <DialogDescription>
                            Create a new document to organize your knowledge and ideas
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="document-type">Document Type</Label>
                            <Select value={formData.type}
                                    onValueChange={(value) => setFormData({...formData, type: value})}>
                                <SelectTrigger id="document-type" data-testid="select-document-type">
                                    <SelectValue/>
                                </SelectTrigger>
                                <SelectContent>
                                    {documentTypes.map((type) => (
                                        <SelectItem key={type.value} value={type.value}>
                                            <div className="flex items-center gap-2">
                                                <type.icon className="h-4 w-4" style={{color: type.color}}/>
                                                <div>
                                                    <div className="font-medium">{type.label}</div>
                                                    <div
                                                        className="text-xs text-muted-foreground">{type.description}</div>
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="document-title">Title</Label>
                            <Input
                                id="document-title"
                                placeholder="Enter document title..."
                                value={formData.title}
                                onChange={(e) => {
                                    onUserEditedTitle();
                                    setFormData({...formData, title: e.target.value});
                                }}
                                data-testid="input-document-title"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Content</Label>
                            <RichTextEditor
                                content={formData.content}
                                onChange={(content) => setFormData({...formData, content})}
                                data-testid="editor-document-content"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsCreateDialogOpen(false);
                                setFormData({title: "", type: "note", content: ""});
                            }}
                            data-testid="button-cancel-create"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateDocument}
                            disabled={createDocumentMutation.isPending}
                            data-testid="button-save-document"
                        >
                            Create Document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Move to Space Dialog */}
            <Dialog open={isMoveToSpaceDialogOpen} onOpenChange={setIsMoveToSpaceDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Move Document to Space</DialogTitle>
                        <DialogDescription>
                            Select a space to move "{documentToMove?.title}" to. The document will be removed from the current space.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="move-space-select">Select Space</Label>
                            <Select
                                value={selectedSpaceId}
                                onValueChange={setSelectedSpaceId}
                            >
                                <SelectTrigger id="move-space-select" data-testid="select-move-space">
                                    <SelectValue placeholder="Choose a space..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {allSpaces
                                        .filter(s => s.id !== spaceId) // Exclude current space
                                        .map((space) => (
                                            <SelectItem key={space.id} value={space.id}>
                                                <div className="flex items-center gap-2">
                                                    <Layers className="h-4 w-4" />
                                                    <span>{space.name}</span>
                                                </div>
                                            </SelectItem>
                                        ))}
                                    {allSpaces.filter(s => s.id !== spaceId).length === 0 && (
                                        <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                            No other spaces available
                                        </div>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setIsMoveToSpaceDialogOpen(false);
                                setDocumentToMove(null);
                                setSelectedSpaceId("");
                            }}
                            data-testid="button-cancel-move"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleMoveToSpace}
                            disabled={moveToSpaceMutation.isPending || !selectedSpaceId}
                            data-testid="button-confirm-move"
                        >
                            {moveToSpaceMutation.isPending ? "Moving..." : "Move Document"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <PinVerificationDialog
                open={showPinDialogForDoc}
                onOpenChange={(open) => {
                    if (!open) {
                        setShowPinDialogForDoc(false);
                        setPendingDocumentIdToOpen(null);
                    }
                }}
                onSuccess={handleDocPinSuccess}
                title="Unlock Document"
                description="This document is locked. Enter your PIN to view its contents."
            />

            <DocumentViewerModal
                open={!!documentToView}
                onOpenChange={(open) => { if (!open) setDocumentToView(null); }}
                document={documentToView}
            />
        </div>
    );
}
